/**
 * Fleet Notifications Hook
 *
 * Manages shore-to-vessel and vessel-to-shore notifications.
 * Offline-first: stores locally, syncs when online.
 *
 * Features:
 * - Shore edit notifications to Master
 * - Acknowledgement workflow
 * - Query/response for disputed changes
 * - Escalation tracking
 * - Three-timestamp model (editedAt, deliveredAt, acknowledgedAt)
 *
 * @see FLEET-DASHBOARD-DESIGN.md for full specification
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useStorageState } from '../../useStorageState';
import { usePermissions } from '../../usePermissions';
import { useVessel } from '../../../contexts/VesselContext';

/**
 * Generate unique notification ID
 */
const generateNotificationId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `notif_${timestamp}_${random}`;
};

/**
 * Generate unique acknowledgement ID
 */
const generateAckId = (notificationId) => {
  const timestamp = Date.now();
  return `ack_${notificationId}_${timestamp}`;
};

/**
 * Hook for managing fleet notifications
 *
 * @param {string} vesselId - Optional specific vessel ID (defaults to current)
 * @returns {Object} Notification management functions and state
 */
export const useFleetNotifications = (vesselId = null) => {
  const { currentUser, isFleet } = usePermissions();
  const { currentVesselId, vessels } = useVessel();

  // Use provided vesselId or current
  const targetVesselId = vesselId || currentVesselId;

  // Notifications for this vessel (vessel-scoped)
  const { value: notifications, setValue: setNotifications } = useStorageState(
    'fleet_notifications',
    [],
    { noScope: false } // Vessel-scoped
  );

  // Acknowledgements pending sync (vessel-scoped)
  const { value: pendingAcks, setValue: setPendingAcks } = useStorageState(
    'fleet_notification_acks',
    [],
    { noScope: false }
  );

  // Shore edit audit trail (fleet-wide for shore visibility)
  const { value: auditTrail, setValue: setAuditTrail } = useStorageState(
    'shore_edit_audit',
    [],
    { noScope: true }
  );

  /**
   * Create a shore edit notification
   * Called when shore user edits vessel data
   */
  const createShoreEditNotification = useCallback((changeDetails) => {
    if (!currentUser || !targetVesselId) {
      console.warn('Cannot create notification: missing user or vessel');
      return null;
    }

    const notification = {
      id: generateNotificationId(),
      vesselId: targetVesselId,

      // Content
      type: 'shore_edit',
      priority: changeDetails.priority || 'medium',
      title: changeDetails.title || 'Shore Edit',
      message: changeDetails.message || '',

      // Change details
      change: {
        module: changeDetails.module,
        field: changeDetails.field,
        itemId: changeDetails.itemId,
        itemName: changeDetails.itemName,
        oldValue: changeDetails.oldValue,
        newValue: changeDetails.newValue,
        reason: changeDetails.reason || ''
      },

      // Source tracking
      editedBy: {
        userId: currentUser.id,
        name: currentUser.name || 'Unknown User',
        role: currentUser.role
      },
      entrySource: 'SHORE',
      entryMethod: 'FLEET_DASHBOARD',

      // Three-timestamp model
      editedAt: new Date().toISOString(),
      deliveredAt: null,  // Set when vessel syncs
      acknowledgedAt: null,

      // Acknowledgement details
      acknowledgedBy: null,
      acknowledgementType: null,

      // Query workflow
      query: {
        queriedAt: null,
        queryText: null,
        respondedAt: null,
        responseText: null,
        resolvedAt: null
      },

      // Sync metadata
      syncStatus: {
        status: 'local_only',
        lastSyncAttempt: null,
        syncError: null
      },

      // Escalation tracking
      escalations: []
    };

    // Add to notifications
    setNotifications(prev => [...(prev || []), notification]);

    // Add to audit trail
    const auditEntry = {
      id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      action: 'SHORE_EDIT',
      ...notification.change,
      editedBy: notification.editedBy,
      entrySource: notification.entrySource,
      entryMethod: notification.entryMethod,
      editReason: notification.change.reason,
      editedAt: notification.editedAt,
      notificationId: notification.id,
      vesselId: targetVesselId,
      vesselNotified: true,
      vesselDeliveredAt: null,
      vesselAcknowledged: false,
      vesselAcknowledgedAt: null,
      vesselAcknowledgedBy: null
    };

    setAuditTrail(prev => [...(prev || []), auditEntry]);

    // Emit event for sync bridge
    window.dispatchEvent(new CustomEvent('sr:notification:created', {
      detail: notification
    }));

    return notification;
  }, [currentUser, targetVesselId, setNotifications, setAuditTrail]);

  /**
   * Acknowledge a notification
   * Called by Master when they've seen and accepted the change
   */
  const acknowledgeNotification = useCallback((notificationId, response = {}) => {
    if (!currentUser) {
      console.warn('Cannot acknowledge: no current user');
      return false;
    }

    const now = new Date().toISOString();

    // Update notification
    setNotifications(prev => (prev || []).map(notif => {
      if (notif.id !== notificationId) return notif;

      return {
        ...notif,
        acknowledgedAt: now,
        acknowledgedBy: {
          userId: currentUser.id,
          name: currentUser.name || 'Unknown User',
          role: currentUser.role
        },
        acknowledgementType: 'accepted',
        syncStatus: {
          ...notif.syncStatus,
          status: 'local_only' // Needs to sync
        }
      };
    }));

    // Create acknowledgement record for sync
    const ack = {
      id: generateAckId(notificationId),
      notificationId,
      vesselId: targetVesselId,
      acknowledgedBy: currentUser.id,
      acknowledgedByName: currentUser.name || 'Unknown User',
      acknowledgedAt: now,
      action: response.action || 'acknowledged',
      notes: response.notes || null,
      syncStatus: {
        status: 'local_only',
        lastSyncAttempt: null
      }
    };

    setPendingAcks(prev => [...(prev || []), ack]);

    // Update audit trail
    setAuditTrail(prev => (prev || []).map(entry => {
      if (entry.notificationId !== notificationId) return entry;

      return {
        ...entry,
        vesselAcknowledged: true,
        vesselAcknowledgedAt: now,
        vesselAcknowledgedBy: currentUser.name
      };
    }));

    // Emit event for sync
    window.dispatchEvent(new CustomEvent('sr:notification:acknowledged', {
      detail: { notificationId, ack }
    }));

    return true;
  }, [currentUser, targetVesselId, setNotifications, setPendingAcks, setAuditTrail]);

  /**
   * Query/dispute a notification
   * Called by Master when they want to challenge a shore edit
   */
  const queryNotification = useCallback((notificationId, queryText) => {
    if (!currentUser || !queryText) {
      console.warn('Cannot query: missing user or query text');
      return false;
    }

    const now = new Date().toISOString();

    setNotifications(prev => (prev || []).map(notif => {
      if (notif.id !== notificationId) return notif;

      return {
        ...notif,
        acknowledgementType: 'queried',
        query: {
          ...notif.query,
          queriedAt: now,
          queryText
        },
        syncStatus: {
          ...notif.syncStatus,
          status: 'local_only'
        }
      };
    }));

    // Emit event for sync
    window.dispatchEvent(new CustomEvent('sr:notification:queried', {
      detail: { notificationId, queryText }
    }));

    return true;
  }, [currentUser, setNotifications]);

  /**
   * Respond to a query (shore side)
   */
  const respondToQuery = useCallback((notificationId, responseText) => {
    if (!currentUser || !isFleet) {
      console.warn('Cannot respond: must be fleet user');
      return false;
    }

    const now = new Date().toISOString();

    setNotifications(prev => (prev || []).map(notif => {
      if (notif.id !== notificationId) return notif;

      return {
        ...notif,
        query: {
          ...notif.query,
          respondedAt: now,
          responseText
        },
        syncStatus: {
          ...notif.syncStatus,
          status: 'local_only'
        }
      };
    }));

    return true;
  }, [currentUser, isFleet, setNotifications]);

  /**
   * Resolve a query (vessel side)
   * Called by Master after reading shore's response to mark the query workflow complete
   * Required for ISM Code Section 9 compliance - complete audit trail closure
   */
  const resolveQuery = useCallback((notificationId) => {
    if (!currentUser) {
      console.warn('Cannot resolve: no current user');
      return false;
    }

    const now = new Date().toISOString();

    setNotifications(prev => (prev || []).map(notif => {
      if (notif.id !== notificationId) return notif;

      // Only resolve if it's a queried notification with a response
      if (notif.acknowledgementType !== 'queried' || !notif.query?.respondedAt) {
        console.warn('Cannot resolve: notification not in queried state with response');
        return notif;
      }

      return {
        ...notif,
        acknowledgedAt: now,
        acknowledgedBy: {
          userId: currentUser.id,
          name: currentUser.name || 'Unknown User',
          role: currentUser.role
        },
        acknowledgementType: 'resolved_after_query',
        query: {
          ...notif.query,
          resolvedAt: now
        },
        syncStatus: {
          ...notif.syncStatus,
          status: 'local_only'
        }
      };
    }));

    // Update audit trail
    setAuditTrail(prev => (prev || []).map(entry => {
      if (entry.notificationId !== notificationId) return entry;

      return {
        ...entry,
        vesselAcknowledged: true,
        vesselAcknowledgedAt: now,
        vesselAcknowledgedBy: currentUser.name,
        resolvedViaQuery: true
      };
    }));

    // Emit event for sync
    window.dispatchEvent(new CustomEvent('sr:notification:resolved', {
      detail: { notificationId }
    }));

    return true;
  }, [currentUser, setNotifications, setAuditTrail]);

  /**
   * Mark notification as delivered (called on sync)
   */
  const markDelivered = useCallback((notificationId) => {
    const now = new Date().toISOString();

    setNotifications(prev => (prev || []).map(notif => {
      if (notif.id !== notificationId) return notif;
      if (notif.deliveredAt) return notif; // Already delivered

      return {
        ...notif,
        deliveredAt: now,
        syncStatus: {
          ...notif.syncStatus,
          status: 'synced'
        }
      };
    }));

    // Update audit trail
    setAuditTrail(prev => (prev || []).map(entry => {
      if (entry.notificationId !== notificationId) return entry;

      return {
        ...entry,
        vesselDeliveredAt: now
      };
    }));
  }, [setNotifications, setAuditTrail]);

  /**
   * Get unread/unacknowledged notifications
   */
  const unacknowledgedNotifications = useMemo(() => {
    return (notifications || []).filter(n => !n.acknowledgedAt && n.type === 'shore_edit');
  }, [notifications]);

  /**
   * Get notifications with pending queries
   */
  const queriedNotifications = useMemo(() => {
    return (notifications || []).filter(n =>
      n.acknowledgementType === 'queried' &&
      n.query?.queriedAt &&
      !n.query?.resolvedAt
    );
  }, [notifications]);

  /**
   * Get all notifications for current vessel
   */
  const allNotifications = useMemo(() => {
    return (notifications || []).sort((a, b) =>
      new Date(b.editedAt) - new Date(a.editedAt)
    );
  }, [notifications]);

  /**
   * Get notifications across all vessels (fleet view)
   */
  const getFleetNotifications = useCallback(() => {
    if (!isFleet) return [];

    // Read from all vessel keys
    const allNotifs = [];

    vessels.forEach(vessel => {
      const key = `fleet_notifications_${vessel.id}`;
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const vesselNotifs = JSON.parse(raw);
          allNotifs.push(...vesselNotifs.map(n => ({
            ...n,
            vesselName: vessel.name
          })));
        }
      } catch (e) {
        console.warn('Error reading notifications for vessel:', vessel.id, e);
      }
    });

    return allNotifs.sort((a, b) =>
      new Date(b.editedAt) - new Date(a.editedAt)
    );
  }, [isFleet, vessels]);

  /**
   * Check for escalation needs (called periodically)
   */
  const checkEscalations = useCallback(() => {
    const now = new Date();
    const updated = [];

    setNotifications(prev => (prev || []).map(notif => {
      if (notif.acknowledgedAt) return notif; // Already acknowledged
      if (notif.type !== 'shore_edit') return notif;

      const editedAt = new Date(notif.editedAt);
      const hoursSinceEdit = (now - editedAt) / (1000 * 60 * 60);

      const escalations = [...(notif.escalations || [])];
      let needsUpdate = false;

      // 48 hour escalation
      if (hoursSinceEdit >= 48 && !escalations.find(e => e.level === 1)) {
        escalations.push({
          level: 1,
          triggeredAt: now.toISOString(),
          action: 'REMINDER_SENT'
        });
        needsUpdate = true;
      }

      // 7 day escalation
      if (hoursSinceEdit >= 168 && !escalations.find(e => e.level === 2)) {
        escalations.push({
          level: 2,
          triggeredAt: now.toISOString(),
          action: 'DPA_ALERTED'
        });
        needsUpdate = true;
      }

      // 14 day escalation
      if (hoursSinceEdit >= 336 && !escalations.find(e => e.level === 3)) {
        escalations.push({
          level: 3,
          triggeredAt: now.toISOString(),
          action: 'COMPLIANCE_FLAG'
        });
        needsUpdate = true;
      }

      if (needsUpdate) {
        updated.push(notif.id);
        return { ...notif, escalations };
      }

      return notif;
    }));

    return updated;
  }, [setNotifications]);

  // Run escalation check on mount and periodically
  useEffect(() => {
    checkEscalations();

    // Check every hour
    const interval = setInterval(checkEscalations, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkEscalations]);

  return {
    // State
    notifications: allNotifications,
    unacknowledgedNotifications,
    queriedNotifications,
    unacknowledgedCount: unacknowledgedNotifications.length,
    pendingAcks,

    // Actions
    createShoreEditNotification,
    acknowledgeNotification,
    queryNotification,
    respondToQuery,
    resolveQuery,
    markDelivered,

    // Fleet-wide queries
    getFleetNotifications,

    // Maintenance
    checkEscalations,

    // Audit
    auditTrail
  };
};

export default useFleetNotifications;
